/**
 * What the platform says when the user's entity is Inactive (2026-10-10): at sign-in, for their default entity,
 * and on any request sent with `X-Entity-Id` naming one -- and what the entity picker says when one is chosen.
 */
export const ENTITY_INACTIVE_MESSAGE = 'Please contact your administrator.';

/** The platform's error code for that refusal. */
export const ENTITY_INACTIVE_CODE = 'ENTITY_INACTIVE';

/**
 * The Login page's alert when sign-in is refused because none of the user's entities is Active (2026-10-15) --
 * typically a user whose one entity is Inactive. Worded and drawn as `AccessError.png`: a titled red box above the
 * form, not the plain error line a wrong password gets.
 */
export const ENTITY_ACCESS_ERROR_TITLE = 'Access not available';
export const ENTITY_ACCESS_ERROR_MESSAGE =
  "Your account isn't mapped to any active entity. Please contact your administrator.";

/**
 * Whether a failed sign-in (password or one-time code) is that refusal.
 *
 * Two shapes reach the sign-in saga. The raw response, with the platform's `ENTITY_INACTIVE` code; and -- the usual
 * one -- the `ApiError` the response interceptor has already normalised it to, which keeps the status and message but
 * not the code (2026-10-16: reading the raw shape alone missed it, so the Login page showed the bare "Please contact
 * your administrator."). On sign-in that message is this refusal's alone: an inactive account is answered "Invalid
 * username or password".
 */
export function isEntityInactiveSignIn(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;
  const raw = error as { response?: { data?: unknown }; message?: unknown; status?: unknown };
  if (isEntityInactiveResponse(raw.response?.data)) return true;
  return (
    typeof raw.message === 'string' &&
    raw.message.trim() === ENTITY_INACTIVE_MESSAGE &&
    (raw.status === 401 || raw.status === 403)
  );
}

/**
 * Whether a failed response is that refusal: the code on the body, under whichever name the envelope uses, or
 * failing that the message itself.
 */
export function isEntityInactiveResponse(data: unknown): boolean {
  if (typeof data !== 'object' || data === null) return false;
  const body = data as { code?: unknown; errorCode?: unknown; message?: unknown };
  if (body.code === ENTITY_INACTIVE_CODE || body.errorCode === ENTITY_INACTIVE_CODE) return true;
  return typeof body.message === 'string' && body.message.trim() === ENTITY_INACTIVE_MESSAGE;
}
